import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import LoadingSpinner from '../common/LoadingSpinner'
import axios from 'axios'

const ProofUploadModal = ({
  isOpen,
  onClose,
  onSuccess,
  gym,
  fromStage,
  toStage
}) => {
  const [uploading, setUploading] = useState(false)
  const [formData, setFormData] = useState({
    notes: '',
    photos: []
  })
  const [photoPreviews, setPhotoPreviews] = useState([])

  // Reset form when modal opens with new gym or stage
  useEffect(() => {
    if (isOpen) {
      setFormData({ notes: '', photos: [] })
      setPhotoPreviews([])
      setUploading(false)
    }
  }, [isOpen, gym?.id, fromStage, toStage])

  const handleNotesChange = (e) => {
    setFormData(prev => ({ ...prev, notes: e.target.value }))
  }

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    // Validate file types - now includes PDF
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
    const invalidFiles = files.filter(file => !validTypes.includes(file.type))
    
    if (invalidFiles.length > 0) {
      toast.error('Please upload only JPEG, JPG, PNG, WebP images, or PDF documents')
      return
    }

    // Validate file sizes (max 10MB for PDF, 5MB for images)
    const oversizedFiles = files.filter(file => {
      const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024
      return file.size > maxSize
    })
    if (oversizedFiles.length > 0) {
      toast.error('Please upload images smaller than 5MB or PDFs smaller than 10MB')
      return
    }

    // Limit to 5 files
    if (formData.photos.length + files.length > 5) {
      toast.error('Maximum 5 files allowed')
      return
    }

    // Create previews
    const newPreviews = []
    files.forEach(file => {
      if (file.type === 'application/pdf') {
        // For PDF files, create a preview with file info
        newPreviews.push({
          file,
          url: null, // No image preview for PDF
          name: file.name,
          type: 'pdf',
          size: file.size
        })
        
        if (newPreviews.length === files.length) {
          setPhotoPreviews(prev => [...prev, ...newPreviews])
          setFormData(prev => ({ 
            ...prev, 
            photos: [...prev.photos, ...files] 
          }))
        }
      } else {
        // For image files, create image preview
        const reader = new FileReader()
        reader.onload = (e) => {
          newPreviews.push({
            file,
            url: e.target.result,
            name: file.name,
            type: 'image'
          })
          
          if (newPreviews.length === files.length) {
            setPhotoPreviews(prev => [...prev, ...newPreviews])
            setFormData(prev => ({ 
              ...prev, 
              photos: [...prev.photos, ...files] 
            }))
          }
        }
        reader.readAsDataURL(file)
      }
    })
  }

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }))
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.notes.trim()) {
      toast.error('Please add notes describing the work completed')
      return
    }

    if (formData.photos.length === 0) {
      toast.error('Please upload at least one photo as proof of work')
      return
    }

    setUploading(true)

    try {
      // Create FormData for file upload
      const uploadData = new FormData()
      uploadData.append('gymId', gym.id)
      uploadData.append('stage', toStage)
      uploadData.append('notes', formData.notes)
      uploadData.append('previousStage', fromStage)
      
      formData.photos.forEach((photo, index) => {
        uploadData.append('photos', photo)
      })

      console.log('Sending stage transition request:', {
        gymId: gym.id,
        stage: toStage,
        notes: formData.notes,
        previousStage: fromStage,
        photosCount: formData.photos.length
      })

      // Upload proof and move stage
      const response = await axios.post('/api/gyms/stage-transition', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.success(`Proof uploaded successfully! Gym moved to ${toStage}`)
      onSuccess(response.data)
      onClose()
    } catch (error) {
      console.error('Error uploading proof:', error)
      console.error('Error response:', error.response?.data)
      const message = error.response?.data?.error || 'Failed to upload proof'
      const details = error.response?.data?.details
      toast.error(message + (details ? `: ${details}` : ''))
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    if (!uploading) {
      setFormData({ notes: '', photos: [] })
      setPhotoPreviews([])
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Upload Proof of Work
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Moving "{gym.name}" from {fromStage} to {toStage}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={uploading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Notes Section */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Work Completed Notes *
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={handleNotesChange}
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Describe the work completed for ${fromStage} stage...`}
              required
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Provide detailed notes about what was accomplished in this stage
            </p>
          </div>

          {/* File Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files * (Max 5 files: Images up to 5MB, PDFs up to 10MB)
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                onChange={handlePhotoChange}
                className="hidden"
                id="photo-upload"
                disabled={uploading}
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="text-gray-400 mb-4">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    Click to upload files
                  </span>
                  <span className="text-gray-500"> or drag and drop</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  JPEG, JPG, PNG, WebP up to 5MB each, or PDF up to 10MB
                </p>
              </label>
            </div>

            {/* Photo Previews */}
            {photoPreviews.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    {preview.type === 'image' ? (
                      <img
                        src={preview.url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-full h-24 flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-red-50">
                        <svg className="w-8 h-8 text-red-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-red-800 font-medium truncate w-full px-1 text-center">
                          PDF
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs hover:bg-red-600 transition-colors"
                      disabled={uploading}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="truncate">{preview.name}</p>
                      <p className="text-gray-300">
                        {preview.type === 'pdf' ? 
                          `${(preview.size / 1024).toFixed(1)} KB` : 
                          'Image'
                        }
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Requirements Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800">Requirements</h4>
                <ul className="mt-1 text-sm text-blue-700 space-y-1">
                  <li>• At least one file (photo or document) showing completed work</li>
                  <li>• Detailed notes explaining what was accomplished</li>
                  <li>• Files must be clear and relevant to the stage</li>
                  <li className="font-medium text-orange-700">• Live photos only. Gallery uploads are blocked.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !formData.notes.trim() || formData.photos.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {uploading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Uploading...
                </>
              ) : (
                `Move to ${toStage}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProofUploadModal 